import { v4 as uuidv4 } from 'uuid';
import { useLiveOrdersStatus } from 'hooks/useLiveOrdersStatus';
import { useLocalStorage } from 'hooks/useLocalStorage/useLocalStorage';
import { useEffect, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from 'state/store';
import { useParams, useSearchParams, useLocation } from 'react-router-dom';
import {
    getByAccessor,
    getCurrentConsumptionModeType,
    getRestaurantId,
    getTableId,
    updateAppState,
} from 'state/app';
import {
    getAskForTableNumber,
    getBrandOptionStatus,
    getWebsiteMetaTitle,
    updateBrand,
} from 'state/brand';
import { updateRestaurantSchedules } from 'state/restaurantSchedules';
import { useGetWeborderingConfigurationQuery } from 'services/brand/brand.endpoints';
import { executeNowAndEveryQuarterHour } from 'utils/refresh';
import { getOpeningDetailsByRestaurants } from 'utils/restaurant/schedule';
import { makeTheme } from 'utils/style';
import { detectIncognito } from 'detectincognitojs';
import { getRouteAttributesFromPath } from 'utils/routes';
import { useTranslation } from 'react-i18next';
import { useCustomFlush } from 'hooks/useCustomFlush';
import { ConsumptionModeType } from '@innovorder/order_detail';
import { getAccessToken, resetCustomer } from 'state/customer/customer.slice';
import { routes } from './routes';

type DetectIncognitoType = {
    isPrivate: boolean;
    browserName: string;
};

export type LocalStorageWOv2 = {
    anonymousId: string | null;
};

export const useAppVM = () => {
    const dispatch = useAppDispatch();
    const params = useParams();
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const queryTableId = searchParams.get('tableId');
    const { value: localStorage, setInLocalStorage } =
        useLocalStorage<LocalStorageWOv2>('webordering-v2');
    const isFoodCourt = useAppSelector(getBrandOptionStatus('FOOD_COURT_MODE'));
    const { flushCart } = useCustomFlush();

    const { pathname } = useLocation();
    const matchedRoute = getRouteAttributesFromPath(pathname, routes);
    const websiteMetaTitle = useAppSelector(getWebsiteMetaTitle);
    const titleAccessorValue = useAppSelector(getByAccessor(matchedRoute?.titleAccessor));
    const currentConsumptionModeType = useAppSelector(getCurrentConsumptionModeType);
    const restaurantId = useAppSelector(getRestaurantId);
    const tableId = useAppSelector(getTableId);

    const askForTableNumber = useAppSelector(getAskForTableNumber(restaurantId));

    const title = useMemo(() => {
        const titles: string[] = [];
        if (matchedRoute) {
            if (titleAccessorValue) titles.push(titleAccessorValue);
            titles.push(t(matchedRoute.name));
        }
        if (websiteMetaTitle) {
            titles.push(websiteMetaTitle);
        }
        return titles.join(' - ');
    }, [titleAccessorValue, matchedRoute, websiteMetaTitle, t]);

    const anonymousId = localStorage?.anonymousId ?? uuidv4();

    const {
        data: weborderingConfiguration,
        isError,
        isLoading,
        error,
    } = useGetWeborderingConfigurationQuery({
        brandHash: params.brandHash ?? 'no-brand',
    });

    const accessToken = useAppSelector(getAccessToken);

    useEffect(() => {
        const isGuestMode =
            weborderingConfiguration?.module_ordering_flows?.[0]?.v2AuthenticationType === 'Guest';
        if (isGuestMode && accessToken) {
            dispatch(resetCustomer(undefined));
        }
    }, [weborderingConfiguration, accessToken, dispatch]);

    useEffect(() => {
        detectIncognito()
            .then(({ isPrivate: isIncognitoMode }: DetectIncognitoType) => {
                dispatch(
                    updateAppState({
                        isIncognitoMode,
                    }),
                );
            })
            .catch(() =>
                dispatch(
                    updateAppState({
                        isIncognitoMode: false,
                    }),
                ),
            );
    }, [dispatch]);

    useEffect(() => {
        dispatch(updateBrand(weborderingConfiguration || null));

        const { clearTimers } = executeNowAndEveryQuarterHour(() => {
            dispatch(
                updateRestaurantSchedules(getOpeningDetailsByRestaurants(weborderingConfiguration)),
            );
        });

        return clearTimers;
    }, [dispatch, weborderingConfiguration]);

    useEffect(() => {
        setInLocalStorage({ anonymousId });
        dispatch(
            updateAppState({
                tableId: queryTableId,
                isFoodCourt,
                anonymousId,
            }),
        );
    }, [queryTableId, isFoodCourt, anonymousId, dispatch, setInLocalStorage]);

    useEffect(() => {
        if (!!queryTableId && !!tableId && String(queryTableId) !== String(tableId)) {
            flushCart();
        }
    }, [queryTableId, tableId, flushCart]);

    useEffect(() => {
        dispatch(
            updateAppState({
                needManualTableId:
                    askForTableNumber &&
                    currentConsumptionModeType === ConsumptionModeType.MODE_SIT_IN,
            }),
        );
    }, [askForTableNumber, currentConsumptionModeType, dispatch]);

    useEffect(() => {
        const handleOffline = () => dispatch(updateAppState({ isDisconnected: true }));
        const handleOnline = () => dispatch(updateAppState({ isDisconnected: false }));

        window.addEventListener('offline', handleOffline);
        window.addEventListener('online', handleOnline);

        return () => {
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('online', handleOnline);
        };
    }, [dispatch]);

    useLiveOrdersStatus();

    const apiTheme = weborderingConfiguration?.module_style_styles.find(
        ({ is_enabled }) => is_enabled,
    );

    const { theme, themeCss } = makeTheme(apiTheme);

    return {
        title,
        theme,
        themeCss,
        error,
        isError,
        isLoading,
        isInitialized: isFoodCourt !== null,
    };
};
